import { useEffect, useRef, useState } from "@wordpress/element";
import { useBlockProps, InnerBlocks } from "@wordpress/block-editor";
import { useSelect, useDispatch } from "@wordpress/data";
import { __ } from "@wordpress/i18n";
import classNames from "classnames";

import Settings from "./Settings";
import LoadingSkeleton from "../components/LoadingSkeleton";
import { COMPARE_FIELDS_OPTIONS } from "../constants";

export default function Edit(props) {
  const { attributes, clientId } = props;
  const { isPopup, compareFields } = attributes;
  const hasInitialized = useRef(false);
  const [compareData, setCompareData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const blockProps = useBlockProps({
    "data-compare-fields": compareFields.join(","),
    className: classNames("brandy-compare-table-editor", {
      "brandy-compare-show-image": compareFields.includes("image"),
      "brandy-compare-show-description": compareFields.includes("description"),
      "brandy-compare-show-price": compareFields.includes("price"),
      "brandy-compare-show-rating": compareFields.includes("rating"),
      "brandy-compare-show-add-to-cart": compareFields.includes("add_to_cart"),
      "has-products": compareData.length > 0,
    }),
  });

  // Get inner blocks and dispatch functions
  const { innerBlocks } = useSelect(
    (select) => ({
      innerBlocks: select("core/block-editor").getBlocks(clientId),
    }),
    [clientId]
  );

  const { replaceInnerBlocks } = useDispatch("core/block-editor");

  // Create template for button block
  const getButtonTemplate = () => [
    [
      "brandy/button",
      {
        buttonText: "View Compare Table",
        className: "compare-table-trigger-button",
        lock: {
          remove: true,
          move: true,
        },
      },
    ],
  ];

  // Function to add button block when isPopup is true
  const addButtonBlock = () => {
    const template = getButtonTemplate();
    const buttonBlock = wp.blocks.createBlock(template[0][0], template[0][1]);

    if (!innerBlocks || innerBlocks.length === 0) {
      replaceInnerBlocks(clientId, [buttonBlock], false);
    } else {
      // Check if button block already exists
      const hasButtonBlock = innerBlocks.some(
        (block) => block.name === "brandy/button"
      );

      if (!hasButtonBlock) {
        replaceInnerBlocks(clientId, [buttonBlock, ...innerBlocks], false);
      }
    }
  };

  // Function to ensure button block exists and restore it if removed
  const ensureButtonBlockExists = () => {
    if (isPopup && innerBlocks) {
      const hasButtonBlock = innerBlocks.some(
        (block) => block.name === "brandy/button"
      );

      if (!hasButtonBlock) {
        // Button was removed, restore it immediately
        addButtonBlock();
      }
    }
  };

  // Function to remove button block when isPopup is false
  const removeButtonBlock = () => {
    if (innerBlocks && innerBlocks.length > 0) {
      const filteredBlocks = innerBlocks.filter(
        (block) => block.name !== "brandy/button"
      );
      replaceInnerBlocks(clientId, filteredBlocks, false);
    }
  };

  // Effect to handle isPopup changes
  useEffect(() => {
    if (hasInitialized.current) {
      if (isPopup) {
        addButtonBlock();
      } else {
        removeButtonBlock();
      }
    }
  }, [isPopup]);

  // Effect to monitor inner blocks and ensure button exists when isPopup is true
  useEffect(() => {
    if (hasInitialized.current) {
      ensureButtonBlockExists();
    }
  }, [innerBlocks, isPopup]);

  // Initialize the block
  useEffect(() => {
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      if (isPopup) {
        addButtonBlock();
      }
    }
  }, []);

  // Function to handle product removal
  const handleRemoveProduct = async (productId) => {
    try {
      const ajaxUrl =
        window.brandyBlocks?.ajax?.path || "/wp-admin/admin-ajax.php";
      const nonce =
        window.brandyCompareList?.ajax?.nonces?.remove_compare_list_item || "";

      const response = await fetch(
        `${ajaxUrl}?action=brandy_remove_compare_list_item&product_id=${productId}&nonce=${nonce}`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        // Re-fetch data for all compare table blocks (this will update all instances)
        // For editor, we just need to update our local state
        setCompareData((prevData) =>
          prevData.filter((product) => product.id !== parseInt(productId))
        );

        // Trigger a custom event to notify other compare table blocks to refresh
        const refreshEvent = new CustomEvent("compareTableRefresh", {
          detail: { removedProductId: productId },
        });
        window.dispatchEvent(refreshEvent);
      } else {
        console.error("Error removing product:", data.data);
      }
    } catch (err) {
      console.error("Remove product error:", err);
    }
  };

  // Fetch compare list data for editor preview
  useEffect(() => {
    const fetchCompareData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const ajaxUrl =
          window.brandyBlocks?.ajax?.path || "/wp-admin/admin-ajax.php";
        const nonce = window.brandyBlocks?.ajax?.nonce || "";

        const response = await fetch(
          `${ajaxUrl}?action=brandy_get_compare_list_items&_wpnonce=${nonce}`
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (data.success) {
          setCompareData(data.data?.products || []);
        } else {
          setError(data.data || "Failed to load compare data");
        }
      } catch (err) {
        setError(err.message || "Error loading compare table data");
        console.error("Compare table editor AJAX error:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCompareData();
  }, []); // Run once when component mounts

  // Function to render compare table content
  const renderCompareTableContent = () => {
    if (isLoading) {
      return <LoadingSkeleton compareFields={compareFields} />;
    }

    if (error) {
      return (
        <div className="compare-table-error">
          <p>
            {__("Error loading compare table: ", "brandy-blocks")} {error}
          </p>
        </div>
      );
    }

    if (compareData.length === 0) {
      return (
        <div className="compare-table-placeholder">
          <p>{__("No products in compare list", "brandy-blocks")}</p>
        </div>
      );
    }

    return (
      <div className="brandy-compare-table compare-table-content-wrapper">
        <div className="compare-table-container">
          <table className="compare-table">
            <tbody>
              {Object.keys(COMPARE_FIELDS_OPTIONS).map((field) => (
                <tr key={field} className={`compare-row compare-row-${field}`}>
                  <td className="compare-field-label">
                    <strong>{COMPARE_FIELDS_OPTIONS[field]}</strong>
                  </td>
                  {compareData.map((product, index) => (
                    <td
                      key={index}
                      className={`compare-product-cell ${
                        field === "name" ? "compare-product-header" : ""
                      }`}
                    >
                      {field === "name" && (
                        <>
                          <button
                            className="compare-remove-button"
                            onClick={() => handleRemoveProduct(product.id)}
                            title={__(
                              "Remove from comparison",
                              "brandy-blocks"
                            )}
                            aria-label={__(
                              "Remove from comparison",
                              "brandy-blocks"
                            )}
                          >
                            <svg
                              viewBox="0 0 24 24"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                            </svg>
                          </button>
                          <h3
                            className="compare-product-name"
                            dangerouslySetInnerHTML={{ __html: product.name }}
                          />
                        </>
                      )}
                      {field === "image" && (
                        <div
                          className="compare-product-image"
                          dangerouslySetInnerHTML={{ __html: product.image }}
                        />
                      )}
                      {field === "description" && (
                        <p
                          className="compare-product-description"
                          dangerouslySetInnerHTML={{
                            __html: product.description,
                          }}
                        />
                      )}
                      {field === "price" && (
                        <span
                          className="compare-product-price"
                          dangerouslySetInnerHTML={{ __html: product.price }}
                        />
                      )}
                      {field === "rating" && (
                        <div className="compare-product-rating">
                          <span className="stars">
                            {"★".repeat(Math.floor(product.rating))}
                          </span>
                          <span className="rating-value">
                            ({product.rating})
                          </span>
                        </div>
                      )}
                      {field === "add_to_cart" && (
                        <div
                          className="compare-add-to-cart-btn"
                          dangerouslySetInnerHTML={{
                            __html: product.add_to_cart,
                          }}
                        />
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div {...blockProps}>
      <Settings {...props} />
      {isPopup && (
        <InnerBlocks
          allowedBlocks={["brandy/button"]}
          template={getButtonTemplate()}
          templateLock="all"
        />
      )}
      {!isPopup && (
        <div className="compare-table-preview">
          {renderCompareTableContent()}
        </div>
      )}
    </div>
  );
}
